// Heuristic gibberish / spam detector for free-text reviews and comments.
// Mirrors the server-side check_text_gibberish function so users get instant
// feedback before submitting.

export interface GibberishResult {
  ok: boolean;
  reason?: string;
}

const VOWELS = /[aeiouy]/i;

const RARE_CLUSTERS = [
  "dv","vd","sv","vs","dw","wd","fv","vf","kv","vk",
  "pv","vp","gv","vg","bv","vb","zx","xz","sdf","dfv",
  "sdv","wdw","dvd","vdv","svs","fvs",
];

const KEYBOARD_MASH = [
  "asdf","sdfg","dfgh","fghj","ghjk","hjkl",
  "qwer","wert","erty","rtyu","tyui","yuio","uiop",
  "zxcv","xcvb","cvbn","vbnm",
  "poiu","oiuy","iuyt","uytr","ytre","trew","rewq",
  "lkjh","kjhg","jhgf","hgfd","gfds","fdsa",
  "mnbv","nbvc","bvcx","vcxz",
  "qazws","wsxed","edcrf","rfvtg","tgbyh","yhnuj","ujmik","ikolp",
  "qwerty","asdfgh","zxcvbn","wasd","fjdksl","qazwsx",
  "1234","2345","3456","4567","5678","6789","7890",
  "0987","9876","8765","7654","6543","5432","4321",
];

function isKeyboardMash(text: string): boolean {
  const lower = text.toLowerCase();
  return KEYBOARD_MASH.some((m) => lower.includes(m));
}

function isGibberishWord(w: string): boolean {
  if (w.length < 3) return false;
  const hasDigit = /[0-9]/.test(w);
  const noVowel = !VOWELS.test(w) && !hasDigit;
  const consonantRun = /[bcdfghjklmnpqrstvwxz]{5,}/i.test(w);
  const tooLong = w.length > 18;
  const repeatedChar = /(.)\1{3,}/.test(w);
  if (noVowel || consonantRun || tooLong || repeatedChar) return true;
  // Alphanumeric mash: 3+ letters mixed with 2+ digits, not a clean word+id or id+word
  const letters = w.replace(/[^a-z]/gi, "");
  const digits = w.replace(/[^0-9]/g, "");
  if (
    letters.length >= 3 &&
    digits.length >= 2 &&
    w.length >= 6 &&
    !/^[a-z]+[0-9]+$/i.test(w) &&
    !/^[0-9]+[a-z]+$/i.test(w)
  ) {
    return true;
  }
  if (w.length >= 4) {
    const vowelCount = (w.match(/[aeiouy]/gi) || []).length;
    if (vowelCount <= 1 && RARE_CLUSTERS.some((r) => w.includes(r))) return true;
  }
  return false;
}

const REVIEW_MIN_CHARS = 75;
const REVIEW_MIN_WORDS = 15;
const COMMENT_MIN_CHARS = 15;

export function checkReviewQuality(text: string): GibberishResult {
  const trimmed = (text || "").trim();
  // Review body is optional — empty is allowed.
  if (!trimmed) return { ok: true };



  if (trimmed.length < REVIEW_MIN_CHARS) {
    return {
      ok: false,
      reason: `Please write at least ${REVIEW_MIN_CHARS} characters (about ${REVIEW_MIN_WORDS} words) so other students get real detail.`,
    };
  }

  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length < REVIEW_MIN_WORDS) {
    return {
      ok: false,
      reason: `Please write at least ${REVIEW_MIN_WORDS} words describing your experience.`,
    };
  }

  if (/(.)\1{5,}/.test(trimmed)) {
    return { ok: false, reason: "Your review appears to contain repeated or random text. Reviews should describe a real experience." };
  }

  if (isKeyboardMash(trimmed)) {
    return { ok: false, reason: "That looks like keyboard mashing. Please write a real review." };
  }

  if (/^[^A-Za-z0-9]+$/.test(trimmed)) {
    return { ok: false, reason: "Reviews must contain real text, not only symbols or emoji." };
  }

  const cleanWords = words
    .map((w) => w.toLowerCase().replace(/[^a-z0-9']/g, ""))
    .filter((w) => w.length > 0);

  if (cleanWords.length === 0) {
    return { ok: false, reason: "Reviews must contain real text, not only symbols or emoji." };
  }

  if (cleanWords.every(isGibberishWord)) {
    return { ok: false, reason: "Your review appears to contain repeated or random text. Reviews should describe a real experience." };
  }

  // Repeated-word ratio: any single word > 30% of content
  if (cleanWords.length >= 5) {
    const counts = new Map<string, number>();
    for (const w of cleanWords) {
      if (w.length >= 2) counts.set(w, (counts.get(w) || 0) + 1);
    }
    let maxCount = 0;
    for (const c of counts.values()) if (c > maxCount) maxCount = c;
    if (maxCount / cleanWords.length > 0.3) {
      return {
        ok: false,
        reason: "Your review repeats the same words too often — please describe your actual experience.",
      };
    }
  }

  if (cleanWords.length >= 6) {
    const unique = new Set(cleanWords);
    if (unique.size / cleanWords.length < 0.4) {
      return { ok: false, reason: "Your review repeats the same words too often — please describe your actual experience." };
    }
    let suspicious = 0;
    for (const w of cleanWords) if (isGibberishWord(w)) suspicious++;
    if (suspicious / cleanWords.length > 0.35) {
      return { ok: false, reason: "Your review appears to contain repeated or random text. Reviews should describe a real experience." };
    }
    const avgLen = cleanWords.reduce((s, w) => s + w.length, 0) / cleanWords.length;
    if (avgLen < 2.2 || avgLen > 12) {
      return { ok: false, reason: "Your review appears to contain repeated or random text. Reviews should describe a real experience." };
    }
  }

  return { ok: true };
}

export function checkCommentQuality(text: string): GibberishResult {
  const trimmed = (text || "").trim();
  if (!trimmed) return { ok: false, reason: "Please write a comment before posting." };
  if (trimmed.length < COMMENT_MIN_CHARS) {
    return { ok: false, reason: `Comments must be at least ${COMMENT_MIN_CHARS} characters. Please add a bit more detail.` };
  }
  if (/(.)\1{5,}/.test(trimmed)) {
    return { ok: false, reason: "Your comment appears to contain repeated or random text. Please write a real comment." };
  }
  if (isKeyboardMash(trimmed)) {
    return { ok: false, reason: "That looks like keyboard mashing. Please write a real comment." };
  }
  if (/^[^A-Za-z0-9]+$/.test(trimmed)) {
    return { ok: false, reason: "Comments must contain real text, not only symbols or emoji." };
  }
  const cleanWords = trimmed
    .split(/\s+/)
    .map((w) => w.toLowerCase().replace(/[^a-z0-9']/g, ""))
    .filter((w) => w.length > 0);
  if (cleanWords.length === 0) {
    return { ok: false, reason: "Comments must contain real text, not only symbols or emoji." };
  }
  if (cleanWords.every(isGibberishWord)) {
    return { ok: false, reason: "Your comment appears to contain repeated or random text. Please write a real comment." };
  }
  if (cleanWords.length >= 5) {
    const counts = new Map<string, number>();
    for (const w of cleanWords) if (w.length >= 2) counts.set(w, (counts.get(w) || 0) + 1);
    let maxCount = 0;
    for (const c of counts.values()) if (c > maxCount) maxCount = c;
    if (maxCount / cleanWords.length > 0.3) {
      return { ok: false, reason: "Your comment repeats the same words too often. Please add more detail." };
    }
  }
  return { ok: true };
}

// Short single-field inputs (names, orgs, titles). Allows short legit values.
export function checkFieldQuality(text: string, fieldLabel: string): GibberishResult {
  const trimmed = (text || "").trim();
  if (!trimmed) return { ok: true };

  if (/(.)\1{5,}/.test(trimmed)) {
    return { ok: false, reason: `${fieldLabel} looks like random characters. Please enter a real value.` };
  }
  if (isKeyboardMash(trimmed)) {
    return { ok: false, reason: `${fieldLabel} looks like keyboard mashing. Please enter a real value.` };
  }
  const words = trimmed
    .split(/\s+/)
    .map((w) => w.toLowerCase().replace(/[^a-z0-9']/g, ""))
    .filter((w) => w.length > 0);
  if (words.length === 0) return { ok: true };
  if (words.every(isGibberishWord)) {
    return { ok: false, reason: `${fieldLabel} looks like random characters. Please enter a real value.` };
  }
  return { ok: true };
}
